import React, { useMemo } from 'react';
import { Check, Layers, LockKeyhole, Mail, PackagePlus, Palette, Plus, ShieldCheck, ShoppingBag, ShoppingCart } from 'lucide-react';
import { shopApi, track } from './api.js';
import { Photo, normalizeOptions, openCart, useColorChoice } from './commerce.js';
import Fulfillment from './Fulfillment.jsx';
import Wishlist from './Wishlist.jsx';
import './commerce.css';

// The purchase controls on a product page: colour, model, the two ways to buy, and what the customer is told.
// `cart` is the object returned by useCart. With `compact` it is a single button (sticky bar, launch page).
// `onOpenProduct(slug, { id, name, source })` opens another product for a model choice; without it the page reloads there.
export default function BuyBox({ product, payment, cart, onPrivacy, onOpenProduct, compact = false }) {
  const { colors, models, addons } = useMemo(() => normalizeOptions(product), [product]);
  const [colorId, chooseColor] = useColorChoice(product?.slug);
  // Add-ons picked on this page, shared by every buy box on it like the colour (stored as a list of ids).
  const [addonIds, chooseAddons] = useColorChoice(`${product?.slug}#addons`);
  if (!product) return null;
  const picked = addons.filter((option) => addonIds.split(',').includes(option.id));
  const toggleAddon = (option) => {
    const on = !picked.includes(option);
    chooseAddons((on ? [...picked, option] : picked.filter((item) => item !== option)).map((item) => item.id).join(','));
    track('store_product_addon_select', product.id, { addon: option.label, on }, { product_slug: product.slug });
  };
  // An add-on is its own product in the shop: picking it puts that product in the cart beside this one.
  const addPicked = async () => {
    for (const option of picked) {
      if (!option.product_slug) continue;
      try {
        const result = await shopApi(`/public/store/products/${encodeURIComponent(option.product_slug)}`);
        if (result?.product?.purchase_availability === 'available') cart.add(result.product);
      } catch { /* the add-on is not on sale right now; the main product is still added */ }
    }
  };

  // The first colour is the choice until the customer picks another, so a product with colours always has one.
  const color = colors.find((option) => option.id === colorId) || colors[0];
  const state = product.purchase_availability;
  const beta = state === 'beta';
  const square = (product.checkout_provider || payment?.provider || 'stripe') === 'square';
  const payable = product.checkout_available !== false;
  const busy = cart.busy;
  const addDisabled = busy || Boolean(state && state !== 'available');
  const buyDisabled = busy || state === 'unavailable' || beta;
  const buyLabel = beta ? 'Not on sale yet' : product.availability === 'Request quote' ? 'Request quote' : cart.cash ? 'Place order, pay cash' : square ? payable ? 'Buy with Square' : 'Contact / payment pending' : 'Start checkout';
  const BuyIcon = payable ? ShoppingBag : Mail;
  const add = async () => { cart.add(product, color); await addPicked(); };
  // With add-ons picked there is more than one product, so they all go to the cart for one checkout.
  const buy = async () => { if (picked.length) { await add(); openCart(); } else cart.buyNow(product, color); };

  if (compact) {
    if (beta) return <button type="button" className="shop-btn buy-compact" disabled>Coming soon</button>;
    // A quote-only product cannot go in the cart, so its one button is the request.
    if (state === 'quote' || product.availability === 'Request quote') return <button type="button" className="shop-btn buy-compact" disabled={buyDisabled} aria-busy={busy || undefined} onClick={buy}>
      {busy ? <span className="shop-spinner buy-spin" aria-hidden="true" /> : <Mail aria-hidden="true" />}Request quote
    </button>;
    return <button type="button" className="shop-btn buy-compact" disabled={addDisabled} onClick={add}><ShoppingCart aria-hidden="true" />Add to cart</button>;
  }

  const openModel = (option) => {
    if (onOpenProduct) return onOpenProduct(option.product_slug, { id: option.product_id, name: option.label, source: 'model_option' });
    track('store_product_view', option.product_id, { source: 'model_option' }, { product_slug: option.product_slug, product_name: option.label });
    return location.assign(`/store/products/${option.product_slug}`);
  };
  const textured = colors.some((option) => option.image_url);
  // What is being chosen: colours by default, or whatever the product says (for example "Front storage").
  const choiceLabel = String(product.viewer_config?.choice_label || '').trim();
  const providerNote = !cart.message && square && payment?.checkout_message;

  return <div className="buy-box">
    {colors.length > 0 && <section className="buy-group">
      <header className="buy-group-head"><span>{choiceLabel ? <Layers aria-hidden="true" /> : <Palette aria-hidden="true" />}{choiceLabel || 'Color & texture'}</span><strong>{color?.label}</strong></header>
      <div className={choiceLabel ? 'buy-swatches is-versions' : 'buy-swatches'} role="group" aria-label={choiceLabel || 'Color and texture'}>
        {colors.map((option) => <button type="button" className="buy-swatch" key={option.id || option.label} aria-pressed={color?.id === option.id} onClick={() => {
          chooseColor(option.id);
          track('store_product_color_select', product.id, { option: option.label }, { product_slug: product.slug });
        }}>
          <span className="buy-swatch-chip"><Photo src={option.image_url} sizes="96px" width={240} /></span>
          <span className="buy-swatch-name">{option.label}</span>
        </button>)}
      </div>
      {color && textured && !color.model_url && <figure className="buy-texture">
        <Photo key={color.image_url} src={color.image_url} alt={choiceLabel ? color.label : `${color.label} color and texture`} sizes="(max-width: 900px) 100vw, 480px" width={720} />
        <figcaption>{choiceLabel ? color.label : `${color.label} texture`}</figcaption>
      </figure>}
    </section>}
    {models.length > 0 && <section className="buy-group">
      <header className="buy-group-head"><span>Model / fitment</span></header>
      <div className="buy-models" role="group" aria-label="Model and fitment">
        {models.map((option) => {
          const current = option.product_slug === product.slug;
          return <button type="button" className="buy-model" key={option.id || option.label} aria-pressed={current} onClick={() => !current && openModel(option)}>{option.label}</button>;
        })}
      </div>
    </section>}
    {addons.length > 0 && <section className="buy-group">
      <header className="buy-group-head"><span><PackagePlus aria-hidden="true" />Add-ons</span><strong>{picked.length ? picked.map((option) => option.label).join(', ') : 'Optional'}</strong></header>
      <div className="buy-addons" role="group" aria-label="Add-ons">
        {addons.map((option) => <button type="button" className="buy-addon" key={option.id || option.label} aria-pressed={picked.includes(option)} onClick={() => toggleAddon(option)}>
          {option.image_url && <span className="buy-addon-chip"><Photo src={option.image_url} sizes="64px" width={160} /></span>}
          <span className="buy-addon-text"><b>{option.label}</b>{option.note && <small>{option.note}</small>}</span>
          <span className="buy-addon-mark" aria-hidden="true">{picked.includes(option) ? <Check /> : <Plus />}</span>
        </button>)}
      </div>
    </section>}
    {!addDisabled && <Fulfillment cart={cart} />}
    {/* Not for sale yet (or not right now): the buy buttons give way to the wishlist. */}
    {beta || state === 'unavailable' ? <Wishlist key={product.slug} product={product} /> : <div className="buy-actions">
      <button type="button" className="shop-btn wide" disabled={addDisabled} onClick={add}><ShoppingCart aria-hidden="true" />{beta ? 'Coming soon' : 'Add to cart'}</button>
      <button type="button" className="shop-btn quiet wide" disabled={buyDisabled} aria-busy={busy || undefined} onClick={buy}>
        {busy ? <span className="shop-spinner buy-spin" aria-hidden="true" /> : <BuyIcon aria-hidden="true" />}{buyLabel}
      </button>
    </div>}
    {/* Always present, with room kept for it, so a message does not push the page around when it appears. */}
    <div className="buy-note" aria-live="polite">
      {cart.message ? <p className="buy-message">{cart.message}</p> : providerNote ? <p className="buy-provider"><ShieldCheck aria-hidden="true" />{providerNote}</p> : null}
    </div>
    {!addDisabled && <p className="buy-final">All sales final; see <a href="/store/returns">Returns &amp; Warranty</a>.</p>}
    {square && <button type="button" className="buy-privacy" onClick={onPrivacy}><LockKeyhole aria-hidden="true" />Square processes payment information and may create an Instant Profile. Read the privacy notice.</button>}
    {product.stl_download_url && <a className="buy-download" href={product.stl_download_url} download onClick={() => track('store_stl_download_click', product.id, {}, { product_slug: product.slug })}>Have a printer? Download the file and print it yourself</a>}
  </div>;
}
